import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Clock,
  Users,
  Timer,
  CalendarCheck,
  TrendingUp,
  Activity,
  ArrowUp,
} from "lucide-react";

import TopPerformerCard from "./components/TopPerformerCard";
import axiosInstance from "./../../axiosInstance";
import { useNavigate } from "react-router-dom";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import AttendanceTimeline from "./components/AttendanceTimeline";
import EmployeeCelebrationCard from "./components/EmployeeCelebrationCard";
import DashboardHeader from "./components/DashboardHeader";
import { useAuth } from "../../contexts/AuthContext";
import "./Dashboard.scss";

const Dashboard = () => {
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [attendanceStatus, setAttendanceStatus] = useState({});
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [leaveData, setLeaveData] = useState([]);
  const [metricsData, setMetricsData] = useState({
    totalEmployees: 0,
    attendanceRate: 0,
    totalWorkedHours: 0,
    totalWorkedMinutes: 0,
    employeeList: [],
  });
  const [liveWorkedTime, setLiveWorkedTime] = useState({
    hours: 0,
    minutes: 0,
  });
  const [shiftRemaining, setShiftRemaining] = useState({
    hours: 0,
    minutes: 0,
  });

  useEffect(() => {
    const updateShiftRemainingTime = () => {
      if (!attendanceStatus?.officeTiming?.shiftTo) {
        setShiftRemaining({ hours: 0, minutes: 0 });
        return;
      }

      const now = new Date();
      const [shiftHour, shiftMinute] = attendanceStatus.officeTiming.shiftTo
        .split(":")
        .map(Number);

      const shiftEnd = new Date();
      shiftEnd.setHours(shiftHour, shiftMinute, 0, 0);

      const diffMs = shiftEnd - now;

      if (diffMs <= 0) {
        setShiftRemaining({ hours: 0, minutes: 0 });
      } else {
        const totalMinutes = Math.floor(diffMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        setShiftRemaining({ hours, minutes });
      }
    };

    updateShiftRemainingTime();
    const interval = setInterval(updateShiftRemainingTime, 60000);
    return () => clearInterval(interval);
  }, [attendanceStatus?.officeTiming?.shiftTo]);

  const navigate = useNavigate();

  const formatDate = (date) => {
    const validDate = new Date(date);
    if (isNaN(validDate.getTime())) return "";
    return validDate.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime24To12 = (time24) => {
    if (!time24) return "";
    const [hour, minute] = time24.split(":");
    const date = new Date();
    date.setHours(+hour, +minute);

    let formatted = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      hour12: true,
    });

    if (minute !== "00") {
      formatted = date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    }

    return formatted.toLowerCase().replace(":00", "").replace(/\s/g, " ");
  };

  const getPunchInDateTime = (raw) => {
    if (!raw) return null;

    // If server already gives a full datetime (ISO or Date or epoch)
    if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw;

    const asNum = Number(raw);
    if (
      !Number.isNaN(asNum) &&
      String(raw)
        .trim()
        .match(/^\d{10,13}$/)
    ) {
      // epoch seconds or ms
      const ms = String(raw).length === 13 ? asNum : asNum * 1000;
      const dt = new Date(ms);
      return isNaN(dt.getTime()) ? null : dt;
    }

    // Try ISO-like string
    const isoTry = new Date(raw);
    if (!isNaN(isoTry.getTime())) return isoTry;

    // Try 12h (h:mm am/pm)
    const m12 = String(raw)
      .trim()
      .match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
    // Try 24h (HH:mm)
    const m24 = String(raw)
      .trim()
      .match(/^(\d{1,2}):(\d{2})$/);

    let hours, minutes;
    if (m12) {
      hours = parseInt(m12[1], 10);
      minutes = parseInt(m12[2], 10);
      const mer = m12[3].toLowerCase();
      if (mer === "pm" && hours !== 12) hours += 12;
      if (mer === "am" && hours === 12) hours = 0;
    } else if (m24) {
      hours = parseInt(m24[1], 10);
      minutes = parseInt(m24[2], 10);
    } else {
      return null;
    }

    // Build a candidate for "today" at that time
    const now = new Date();
    const candidate = new Date(now);
    candidate.setHours(hours, minutes, 0, 0);
    // If that time is in the future (e.g., it's 01:25 and punch-in says "07:00"),
    // assume the punch-in was yesterday at that time.
    if (candidate.getTime() - now.getTime() > 2 * 60 * 1000) {
      candidate.setDate(candidate.getDate() - 1);
    }

    return candidate;
  };

  const isOverShiftTime = () => {
    if (!attendanceStatus.officeTiming?.shiftTo) return false;

    const now = new Date();
    const [shiftHour, shiftMinute] =
      attendanceStatus.officeTiming.shiftTo.split(":");
    const shiftEndTime = new Date();
    shiftEndTime.setHours(parseInt(shiftHour), parseInt(shiftMinute), 0, 0);

    return now > shiftEndTime;
  };

  const hasManagementAuthority = useCallback(() => {
    const managementRoles = [
      "ROLE_HR",
      "ROLE_ADMIN",
      "ROLE_CEO",
      "ROLE_MANAGER",
    ];
    return managementRoles.some((role) => hasRole(role));
  }, [hasRole]);

  useEffect(() => {
    const updateLiveWorkedTime = () => {
      if (!attendanceStatus.punchInTime || !attendanceStatus.attendanceStatus) {
        setLiveWorkedTime({ hours: 0, minutes: 0 });
        return;
      }

      const punchInDateTime = getPunchInDateTime(attendanceStatus.punchInTime);
      if (!punchInDateTime) {
        setLiveWorkedTime({ hours: 0, minutes: 0 });
        return;
      }

      const now = new Date();
      const diffMs = now - punchInDateTime;
      const totalMinutes = Math.floor(diffMs / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      setLiveWorkedTime({
        hours: Math.max(0, hours),
        minutes: Math.max(0, minutes),
      });
    };

    updateLiveWorkedTime();
    const interval = setInterval(updateLiveWorkedTime, 60000);

    return () => clearInterval(interval);
  }, [attendanceStatus.punchInTime, attendanceStatus.attendanceStatus]);

  const calculateMetrics = useCallback(() => {
    const today = new Date();
    const todayStr = today.toDateString();

    let employeeList = [];
    let totalEmployees = 0;

    if (hasRole("ROLE_ADMIN") || hasRole("ROLE_HR") || hasRole("ROLE_CEO")) {
      employeeList = employees;
      totalEmployees = employees.length;
    } else if (hasRole("ROLE_MANAGER")) {
      employeeList = employees;
      totalEmployees = employees.length;
    }

    const todayAttendance = attendanceHistory.filter((record) => {
      const recordDate = new Date(record.attendanceDate);
      return recordDate.toDateString() === todayStr;
    }).length;

    const attendanceRate =
      totalEmployees > 0
        ? Math.round((todayAttendance / totalEmployees) * 100)
        : 0;

    // ✅ Only use liveWorkedTime for active time
    const totalHours = liveWorkedTime.hours;
    const totalMinutes = liveWorkedTime.minutes;

    const newMetrics = {
      totalEmployees,
      attendanceRate,
      totalWorkedHours: totalHours,
      totalWorkedMinutes: totalMinutes,
      employeeList,
    };

    setMetricsData(newMetrics);

    window.dispatchEvent(
      new CustomEvent("metricsUpdated", {
        detail: newMetrics,
      }),
    );
  }, [employees, attendanceHistory, attendanceStatus, hasRole, liveWorkedTime]);

  useEffect(() => {
    calculateMetrics();
  }, [calculateMetrics]);

  const fetchAttendanceStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/status`,
      );

      setAttendanceStatus(response.data?.data || {});
    } catch (error) {
      console.error("Error fetching attendance status:", error);
    }
  }, []);

  const fetchAttendanceHistory = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/All`,
      );

      setAttendanceHistory(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching attendance history:", error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    if (!hasManagementAuthority()) {
      return;
    }

    try {
      const endpoint = hasRole("ROLE_MANAGER")
        ? `${process.env.REACT_APP_API_MANAGER_URL}/employees/manager`
        : `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/All`;

      const response = await axiosInstance.get(endpoint);
      setEmployees(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching employees:", error);
    }
  }, [hasManagementAuthority, hasRole]);

  const fetchAllEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/All`,
      );
      setAllEmployees(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching all employees for celebrations:", error);
    }
  }, []);

  const fetchHolidays = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/holidays/all`,
      );
      setHolidays(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/announcements`,
      );
      setAnnouncements(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching announcements:", error);
    }
  }, []);

  const fetchLeaveData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/leave-history/today`,
      );
      setLeaveData(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching leave data:", error);
    }
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchAttendanceStatus(),
          fetchAttendanceHistory(),
          fetchEmployees(),
          fetchAllEmployees(),
          fetchHolidays(),
          fetchAnnouncements(),
          fetchLeaveData(),
        ]);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [
    fetchAttendanceStatus,
    fetchAttendanceHistory,
    fetchEmployees,
    fetchAllEmployees,
    fetchHolidays,
    fetchAnnouncements,
    fetchLeaveData,
  ]);

  useEffect(() => {
    const intervals = [];

    const attendanceStatusInterval = setInterval(fetchAttendanceStatus, 30000);
    intervals.push(attendanceStatusInterval);

    const attendanceHistoryInterval = setInterval(
      fetchAttendanceHistory,
      300000,
    );
    intervals.push(attendanceHistoryInterval);

    if (hasManagementAuthority()) {
      const employeeInterval = setInterval(fetchEmployees, 900000);
      intervals.push(employeeInterval);
    }

    const allEmployeesInterval = setInterval(fetchAllEmployees, 900000);
    intervals.push(allEmployeesInterval);

    const announcementsInterval = setInterval(fetchAnnouncements, 600000);
    intervals.push(announcementsInterval);

    const leaveDataInterval = setInterval(fetchLeaveData, 600000);
    intervals.push(leaveDataInterval);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchAttendanceStatus();
        calculateMetrics();
      }
    };

    const handleFocus = () => {
      fetchAttendanceStatus();
      calculateMetrics();
    };

    const handleOnline = () => {
      Promise.all([
        fetchAttendanceStatus(),
        fetchAttendanceHistory(),
        fetchEmployees(),
        fetchAllEmployees(),
        fetchHolidays(),
        fetchAnnouncements(),
        fetchLeaveData(),
      ]).then(() => {
        calculateMetrics();
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      intervals.forEach((interval) => clearInterval(interval));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, [
    hasManagementAuthority,
    fetchAttendanceStatus,
    fetchAttendanceHistory,
    fetchEmployees,
    fetchAllEmployees,
    fetchHolidays,
    fetchAnnouncements,
    fetchLeaveData,
    calculateMetrics,
  ]);

  const celebrationData = useMemo(() => {
    const employeeList = allEmployees;
    if (!Array.isArray(employeeList))
      return { birthdays: [], workAnniversaries: [], newJoiners: [] };

    const today = new Date();
    const isToday = (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return (
        today.getMonth() === date.getMonth() &&
        today.getDate() === date.getDate()
      );
    };

    const isThisMonth = (dateString) => {
      if (!dateString) return false;
      const date = new Date(dateString);
      return today.getMonth() === date.getMonth();
    };

    const isWithin15Days = (dateString) => {
      if (!dateString) return false;
      const joinDate = new Date(dateString);
      const diffTime = today.getTime() - joinDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      return diffDays >= -15 && diffDays <= 15;
    };

    const birthdays = employeeList
      .filter((emp) => emp.dob && isThisMonth(emp.dob))
      .map((emp) => ({
        ...emp,
        isToday: isToday(emp.dob),
        celebrationDate: formatDate(emp.dob),
      }))
      .sort((a, b) => new Date(a.dob) - new Date(b.dob));

    const workAnniversaries = employeeList
      .filter((emp) => {
        if (!emp.doj) return false;
        const doj = new Date(emp.doj);
        const yearsCompleted = today.getFullYear() - doj.getFullYear();
        const sameMonth = today.getMonth() === doj.getMonth();
        return yearsCompleted >= 1 && sameMonth;
      })
      .map((emp) => ({
        ...emp,
        isToday: isToday(emp.doj),
        celebrationDate: formatDate(emp.doj),
        yearsCompleted: today.getFullYear() - new Date(emp.doj).getFullYear(),
      }))
      .sort((a, b) => new Date(a.doj) - new Date(b.doj));

    const newJoiners = employeeList
      .filter((emp) => emp.doj && isWithin15Days(emp.doj))
      .map((emp) => ({
        ...emp,
        celebrationDate: formatDate(emp.doj),
        daysAgo: Math.floor(
          (today - new Date(emp.doj)) / (1000 * 60 * 60 * 24),
        ),
      }))
      .sort((a, b) => new Date(b.doj) - new Date(a.doj));

    return { birthdays, workAnniversaries, newJoiners };
  }, [allEmployees]);

  const getWorkingCardColor = () => {
    if (!attendanceStatus?.attendanceStatus || !attendanceStatus?.punchInTime) {
      return user?.customColors?.textMuted || "#94a3b8";
    }

    if (!attendanceStatus.officeTiming?.shiftTo)
      return user?.customColors?.warning || "#f59e0b";

    const now = new Date();
    const [shiftHour, shiftMinute] =
      attendanceStatus.officeTiming.shiftTo.split(":");
    const shiftEndTime = new Date();
    shiftEndTime.setHours(parseInt(shiftHour), parseInt(shiftMinute), 0, 0);

    return now > shiftEndTime
      ? user?.customColors?.success || "#10b981"
      : user?.customColors?.warning || "#f59e0b";
  };

  const handleNoticeClick = (notice) => {
    console.log("Notice clicked:", notice);
    // Add your notice click handler logic here
  };

  const handleCircularClick = (circular) => {
    console.log("Circular clicked:", circular);
    // Add your circular click handler logic here
  };

  const handleLeaveClick = (leave) => {
    console.log("Leave clicked:", leave);
    // Add your leave click handler logic here
    // For example: navigate to leave details page
    // navigate(`/leaves/${leave.leaveId}`);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__inner">
        {/* Header Component */}
        <DashboardHeader
          announcements={announcements}
          metricsData={metricsData}
          attendanceStatus={attendanceStatus}
          liveWorkedTime={liveWorkedTime}
          shiftRemaining={shiftRemaining}
          hasManagementAuthority={hasManagementAuthority}
          holidays={holidays}
          leaveData={leaveData}
          apiBaseUrl={process.env.REACT_APP_API_EMPLOYEE_URL}
          onNoticeClick={handleNoticeClick}
          onCircularClick={handleCircularClick}
          onLeaveClick={handleLeaveClick}
        />

        {/* Main Content Grid */}
        <div className="dashboard-widgets">
          {/* Attendance Timeline */}
          <div className="dashboard-widget dashboard-widget--timeline">
            <div className="dashboard-widget__surface">
              <AttendanceTimeline
                attendanceHistory={attendanceHistory}
                navigate={navigate}
                attendanceStatus={attendanceStatus}
              />
            </div>
          </div>

          {/* Top Performer */}
          <div className="dashboard-widget dashboard-widget--performer">
            <div className="dashboard-widget__performer">
              <TopPerformerCard />
            </div>
          </div>

          {/* Employee Celebrations */}
          <div className="dashboard-widget dashboard-widget--celebrations">
            <EmployeeCelebrationCard celebrationData={celebrationData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
